import { useInkind } from '@gik/api/inkinds/inkind';
import { useUser } from '@gik/api/users/user';
import { signin, switchUserAccount } from '@gik/auth/utils/auth';
import { InViewport } from '@gik/core/components/InViewport';
import { fullDefaultPageImagePath } from '@gik/core/constants';
import { withMeta } from '@gik/core/hocs/withMeta';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import type { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import { ErrorPageSection } from '@gik/core/pages/Error/ErrorPageSection';
import { useInkindCan } from '@gik/core/store/permissions';
import { useUserStore } from '@gik/core/store/UserStore';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import useFinishRegistration from '@gik/finish-registration/hooks/useFinishRegistration';
import { InkindPageTour, inkindPageTourSelectors } from '@gik/inkind-page/components/InkindPageTour/InkindPageTour';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { useInkindStore } from '@gik/inkind-page/store/InkindStore';
import { isInkindPageOrganizer, isInkindPageOwner } from '@gik/inkind-page/utils/inkindPagePermissions';
import { openThankYouFormModal } from '@gik/messaging/thank-you/utils/modals';
import StreamClient from '@gik/stream/utils/StreamClient';
import { useDialogStore } from '@gik/ui/Dialogs';
import { ErrorSection } from '@gik/ui/gik/ErrorSection';
import { useNotificationsContext } from '@gik/ui/Notifications';
import { UI } from '@gik/ui/UIManager';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useLatest from 'react-use/lib/useLatest';
import useSearchParam from 'react-use/lib/useSearchParam';
import { InkindPageContent } from '../InkindPageContent/InkindPageContent';
import { InkindPageHeader } from '../InkindPageHeader/InkindPageHeader';
import InkindPageViewRecorder from '../InkindPageViewRecorder/InkindPageViewRecorder';
import { SamplePageBanner } from '../SamplePageBanner';

export type InkindPageProps = {
  overrideIsSamplePage?: boolean;
};

function InkindPageComp({ overrideIsSamplePage }: InkindPageProps) {
  const bem = bemBlock('inkind-page');
  const { t } = useTranslation();
  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  StreamClient.setCacheHash(inkindRouteId);
  const setIsSamplePage = useInkindStore(state => state.setIsSamplePage);
  const isSamplePage = useInkindStore(state => state.isSamplePage);
  const { data: inkindPage } = useInkind(inkindRouteId);

  // logger.debug('inkindPage', { inkindPage, inkindPageError, inkindPageHeaders });

  const imageUrl = React.useMemo(() => {
    if (inkindPage?.imgSrc?.length > 0) {
      return inkindPage.imgSrc;
    } else if (inkindPage?.cover_src?.length > 0) {
      return inkindPage.cover_src;
    } else {
      return fullDefaultPageImagePath;
    }
  }, [inkindPage]);

  const isMounted = useIsMounted();

  const [forceShowTour, setForceShowTour] = React.useState<boolean>(false);
  const isSamplePageParamValue = useSearchParam('sample-page');
  const userId = useUserStore(state => state.id);
  const { data: user } = useUser(userId);
  const canEdit = useInkindCan('manage', inkindRouteId, inkindPage?.groupId) && isMounted();
  const showSuggestedItemsDisableButton = canEdit;
  const canEditLatest = useLatest(canEdit);
  const orderId = useSearchParam('orderId');
  const claimId = useSearchParam('claimId');
  const supporterEmail = useSearchParam('supporterEmail');
  const supporterFirstName = useSearchParam('supporterFirstName') || 'Supporter';
  const showThankYouModal = useSearchParam('sendThankYou');
  const autoPremium = useSearchParam('autoPremium');

  /**
   * Figure out is this page is a sample page and update store value
   */
  React.useEffect(() => {
    setIsSamplePage(isSamplePageParamValue === 'true' || overrideIsSamplePage);
  }, [isSamplePageParamValue, overrideIsSamplePage, setIsSamplePage]);

  const checkAutoPremium = React.useCallback(() => {
    if (!autoPremium) return;

    (async () => {
      await UI.alert(`Your InKind Page has been upgraded to Premium thanks to ${autoPremium}.`, {
        title: 'Premium Upgrade',
      });

      // strip out url parameters that were used to open this modal
      history.pushState({}, document.title, window.location.pathname);
      setForceShowTour(true);
    })();
  }, [autoPremium]);

  useFinishRegistration(inkindRouteId);

  /**
   * Handle various ways the user arrives on this page.
   *
   * This is a recursive function that calls itself to keep checking privileges until all conditions are satisfied.
   */
  const checkUserPrivileges = React.useCallback(
    (inkindPage: InkindPageAPIModel, userId) => {
      (async () => {
        if (showThankYouModal !== 'true') return;
        // wait till we have inkind page data
        if (!inkindPage) return;

        const isPageOwnerOrOrganizer =
          isInkindPageOwner(inkindPage.owner.userId, userId) || isInkindPageOrganizer(inkindPage, userId);

        const dialogs = useDialogStore.getState().dialogs;
        const notifications = useNotificationsContext.getState().notifications;

        // if the user is logged in but is not a PO then we should ask to switch accounts
        // see: https://www.notion.so/giveinkind/Opening-a-Thank-you-link-when-logged-in-as-a-different-user-non-PO-still-allows-you-to-see-support-5bc564f92347454a84e450acf48cda19

        // update 19 May 2023 -> https://www.notion.so/giveinkind/improvement-allow-recipients-who-are-not-POs-access-to-Thank-You-modal-193ca32f1ab341d6a8d1beb679ba746c
        // Only PO's who are not allowed to edit should be asked to switch account (supporters should not)

        const isPOAndCanEdit = isPageOwnerOrOrganizer && canEditLatest.current === true;
        const userIsPageRecipient = user?.emailAddress?.toLowerCase() === inkindPage.recipientEmail?.toLowerCase();

        if (!showThankYouModal) return;

        // if user is logged in as a PO that can edit or as page recipient
        if (userId) {
          if (dialogs.length === 0) {
            if (isPOAndCanEdit || userIsPageRecipient) {
              openThankYouFormModal(
                {
                  locked: !inkindPage?.isPremium,
                  sender: user,
                  orderId,
                  claimId,
                  recipient: { emailAddress: supporterEmail, firstName: supporterFirstName },
                },
                { inkindRouteId, options: { key: 'thank-you-form' } }
              );
            } else {
              // if user is logged in as a PO that can edit or as page recipient
              // Ask the user to switch accounts if a thank you modal should be shown
              await switchUserAccount();
            }
          }
        } else {
          // show login modal if showThankYouModal should be shown but the user is not logged in
          if (notifications.length === 0) {
            // user not logged in
            UI.notify('Please login to continue to send a Thank You message');
            signin();
          }
        }
      })();
    },
    [showThankYouModal, canEditLatest, user, orderId, claimId, supporterEmail, supporterFirstName, inkindRouteId]
  );

  /**
   * Recheck user privileges if userId changed
   */
  React.useEffect(() => {
    if (inkindPage && (user || !userId) && isMounted()) checkUserPrivileges(inkindPage, userId);
  }, [checkUserPrivileges, inkindPage, isMounted, user, userId]);

  React.useEffect(() => {
    checkAutoPremium();
  }, [checkAutoPremium]);

  if (!inkindRouteId) {
    return <ErrorSection>no routeId specified</ErrorSection>;
  }

  const sixMonthsAgo = moment().subtract(6, 'months');
  const inkindPageCreatedAt = moment(inkindPage?.createdUtc);

  return withMeta(
    {
      title: inkindPage?.title,
      description: inkindPage?.description ?? t(translationKeys.InkindPageInformationIntroEmptyCopy),
      imageUrl,
      imageDimensions:
        imageUrl === fullDefaultPageImagePath
          ? {
              width: 500,
              height: 500,
            }
          : // if dimensions are undefined, Facebook will try to figure it
            // out by itself but thumbnail generation will be delayed
            undefined,
      noIndex: inkindPageCreatedAt.isBefore(sixMonthsAgo),
    },
    <div className={bem()}>
      {isMounted() && <SamplePageBanner className={isSamplePage ? 'gik-sample-page-banner--visible' : null} />}
      {/* TODO: review selectors */}
      {!autoPremium && isMounted() && <InkindPageTour selectors={inkindPageTourSelectors} forceOpen={forceShowTour} />}
      {isSamplePageParamValue !== 'true' && <InkindPageViewRecorder inkindRouteId={inkindRouteId} />}
      <InViewport height={254}>
        <InkindPageHeader />
      </InViewport>
      <InkindPageContent showSuggestedItemsDisableButton={showSuggestedItemsDisableButton} />
    </div>
  );
}

export const InkindPage = withComponentErrorBoundary(InkindPageComp, { ErrorFallback: ErrorPageSection });
